import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  ApiError,
  getThresholds,
  resetThresholds,
  setThresholds,
  type ThresholdsView,
} from '../api.js';
import { formatDate } from '../labels.js';

/**
 * The numbers the rules judge on (`06` §Configuration).
 *
 * WHAT THIS SCREEN IS FOR
 * -----------------------
 * Somebody looks at an amber project and asks "why is that amber?". The answer
 * is on this page, and until now the answer was "because we said so" with no
 * page to point at. So it is readable by anybody who can see the company, and
 * changeable only by an admin.
 *
 * EVERY FIELD PRINTS ITS DEFAULT
 * ------------------------------
 * `06` asks for exactly this — *"the settings screen shows the default beside
 * the current value, so a company can see how far it has drifted from the
 * recommendation"*. It is the one thing that stops these being ordinary
 * preferences. A company that has quietly moved "late" from seven days to
 * thirty has not tuned the product, it has switched a warning off, and the
 * screen should say so on the row where it happened.
 *
 * NOTHING SAVES UNTIL YOU PRESS SAVE — same reasoning as the roles grid above
 * it. Thirteen numbers are one decision about how strict this company is, and
 * saving each keystroke would put thirteen audit entries against it and leave
 * half-applied settings live in between.
 *
 * ONLY WHAT CHANGED IS SENT. The server merges, so two admins on this screen at
 * once do not overwrite each other's untouched fields.
 */

interface Props {
  readonly canEdit: boolean;
}

/** Fields, grouped, in catalogue order — the server decides both. */
function groupsOf(view: ThresholdsView): readonly (readonly [string, ThresholdsView['fields']])[] {
  const order: string[] = [];
  const byGroup = new Map<string, ThresholdsView['fields'][number][]>();
  for (const field of view.fields) {
    if (!byGroup.has(field.group)) {
      byGroup.set(field.group, []);
      order.push(field.group);
    }
    byGroup.get(field.group)!.push(field);
  }
  return order.map((g) => [g, byGroup.get(g)!] as const);
}

export function Thresholds({ canEdit }: Props): JSX.Element {
  const [view, setView] = useState<ThresholdsView | null>(null);
  const [denied, setDenied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  /*
   * Held as STRINGS while being edited.
   *
   * A number input that is being retyped goes through an empty value, and
   * storing that as 0 would flash "0 days" — a legal-looking setting nobody
   * chose — into a field somebody is halfway through changing. Parsed once, on
   * save, where a bad one can be refused with a sentence.
   */
  const [draft, setDraft] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    try {
      const next = await getThresholds();
      setView(next);
      setDraft(Object.fromEntries(next.fields.map((f) => [f.key, String(next.values[f.key])])));
      setDenied(false);
    } catch (caught) {
      const refused = caught instanceof ApiError && caught.status === 403;
      setDenied(refused);
      setError(refused ? null : 'Could not load your settings.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /** Which boxes differ from what is saved. Drives the Save button. */
  const changed = useMemo(() => {
    if (!view) return [] as string[];
    return view.fields
      .filter((f) => draft[f.key] !== String(view.values[f.key]))
      .map((f) => f.key);
  }, [view, draft]);

  const apply = (next: ThresholdsView): void => {
    setView(next);
    setDraft(Object.fromEntries(next.fields.map((f) => [f.key, String(next.values[f.key])])));
  };

  const save = async (): Promise<void> => {
    if (!view) return;
    setBusy(true);
    setError(null);
    setSaved(false);

    // Only the touched ones, and only if they parse. An unparseable box is
    // refused here with a sentence rather than sent as NaN and refused there
    // with the shape of the request.
    const payload: Record<string, number> = {};
    for (const key of changed) {
      const value = Number.parseInt(draft[key] ?? '', 10);
      if (!Number.isFinite(value)) {
        const field = view.fields.find((f) => f.key === key);
        setError(`"${field?.label ?? key}" needs a whole number of days.`);
        setBusy(false);
        return;
      }
      payload[key] = value;
    }

    try {
      apply(await setThresholds(payload));
      setSaved(true);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not save.');
    } finally {
      setBusy(false);
    }
  };

  const putBack = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      apply(await resetThresholds());
      setSaved(true);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  if (denied) {
    return (
      <>
        <h3 className="sub-head">When the app calls something late</h3>
        <p className="hint">Your role does not include your company&rsquo;s settings.</p>
      </>
    );
  }

  return (
    <>
      {/* Named for what it decides, not for what it is. "Thresholds" is our
          word for it; "when the app calls something late" is the question the
          person arriving here is actually trying to answer. */}
      <h3 className="sub-head">When the app calls something late</h3>

      <p className="hint">
        These decide when a project turns amber, when it turns red, and when somebody gets
        chased. Every one shows what we recommend beside it, so you can see how far you have
        moved from it.
      </p>

      {error && (
        <div className="alert" role="alert">
          <span>{error}</span>
        </div>
      )}

      {view === null && !error && <p aria-live="polite">Loading…</p>}

      {view && (
        <>
          {view.changed.length > 0 ? (
            <p className="hint">
              {view.changed.length === 1
                ? 'One setting has been changed from what we recommend'
                : `${view.changed.length} settings have been changed from what we recommend`}
              {view.updated_by && ` by ${view.updated_by}`}
              {view.updated_at && ` on ${formatDate(view.updated_at)}`}.
            </p>
          ) : (
            <p className="hint">Everything is exactly what the product recommends.</p>
          )}

          {saved && changed.length === 0 && (
            <p className="hint" aria-live="polite">
              Saved. Every project is judged on the new numbers from the next time it is opened.
            </p>
          )}

          {groupsOf(view).map(([group, fields]) => (
            <div key={group} className="threshold-group">
              <h4>{group}</h4>

              {fields.map((field) => {
                const recommended = view.defaults[field.key]!;
                const moved = view.values[field.key] !== recommended;
                return (
                  <div key={field.key} className="threshold-row">
                    <label htmlFor={`th-${field.key}`}>
                      {field.label}
                      {moved && (
                        <span className="threshold-mark" title="Changed from what we recommend">
                          {' '}
                          ·
                        </span>
                      )}
                    </label>

                    <div className="threshold-input">
                      <input
                        id={`th-${field.key}`}
                        type="number"
                        inputMode="numeric"
                        min={field.min}
                        max={field.max}
                        step={1}
                        disabled={!canEdit || busy}
                        value={draft[field.key] ?? ''}
                        onChange={(event) =>
                          setDraft((d) => ({ ...d, [field.key]: event.target.value }))
                        }
                        aria-describedby={`th-${field.key}-help`}
                      />
                      {/* The recommendation, on every row, always — not only
                          when it has been moved. A number you can compare
                          against is the difference between a setting and a
                          box with a number in it. */}
                      <span className="threshold-default">we recommend {recommended}</span>
                    </div>

                    <p className="hint" id={`th-${field.key}-help`}>
                      {field.help}
                    </p>
                  </div>
                );
              })}
            </div>
          ))}

          {canEdit && (
            <div className="button-row">
              {/* "Save these numbers", not "Save". This section sits on a page
                  that already has a Save — the company profile's — and two
                  buttons with the same accessible name is a page somebody
                  tabbing through cannot navigate. The heading disambiguates
                  them for an eye; the label has to do it for everybody else. */}
              <button
                type="button"
                className="inline"
                disabled={changed.length === 0 || busy}
                onClick={() => void save()}
              >
                {busy ? 'Saving…' : 'Save these numbers'}
              </button>
              <button
                type="button"
                className="secondary inline"
                disabled={changed.length === 0 || busy}
                onClick={() =>
                  setDraft(
                    Object.fromEntries(view.fields.map((f) => [f.key, String(view.values[f.key])])),
                  )
                }
              >
                Cancel
              </button>
              {view.changed.length > 0 && (
                <button
                  type="button"
                  className="link-button"
                  disabled={busy}
                  onClick={() => void putBack()}
                >
                  Put everything back to what we recommend
                </button>
              )}
            </div>
          )}

          {!canEdit && <p className="hint">Your role can see these, but not change them.</p>}
        </>
      )}
    </>
  );
}
